/** @jsxImportSource @emotion/react */

import type { CSSObject } from "@emotion/react";

import { Color, Palette, PaletteColor, Theme, useTheme } from "@mui/material";
import * as Colors from "@mui/material/colors";
import React from "react";
import type { themeObject } from "../theme/theme";

import { IconsMap } from "./IPlatform.constants";

type ThemeColor = `${keyof typeof themeObject.palette}` | `${keyof Palette}.${keyof PaletteColor}`;
type StandardColor = `${keyof typeof Colors}` | `${keyof typeof Colors}.${keyof Color}`;

export type IPlatformIconNames = keyof typeof IconsMap;

type IPlatformThemeColor = ThemeColor | StandardColor;
type IPlatformColor =
	| IPlatformThemeColor
	| `#${string}`
	| keyof typeof Colors
	| "black"
	| "white"
	| `rgb(${string})`
	| `rgba(${string})`
	| string
	| "inherit";

interface IPlatformIconProps {
	color?: IPlatformColor;
	size?: 4 | 8 | 12 | 16 | 24 | 28 | 32 | 36 | 40 | 48 | 52 | 56 | 60 | 64 | number;
	name?: IPlatformIconNames;
	iconCss?: CSSObject;
}

interface IPlatformIconProps {
	twoColor?: boolean;
	foreground?: {
		name: IPlatformIconNames;
		color: IPlatformColor;
	};
	background?: {
		name: IPlatformIconNames;
		color: IPlatformColor;
	};
	size?: 4 | 8 | 12 | 16 | 24 | 28 | 32 | 36 | 40 | 48 | 52 | 56 | 60 | 64 | number;
	iconCss?: CSSObject;
}

const IPlatformIcon = React.forwardRef<any, IPlatformIconProps>((props, ref) => {
	const { color, size, name, twoColor, foreground, background, iconCss, ...otherProps } = props;
	const theme = useTheme();

	if (!props.twoColor) {
		const customColor = getCustomColorFromTheme(color || "inherit", theme);
		if (!name) throw new Error("missing name property");

		return (
			<i
				ref={ref}
				css={{
					color: customColor,
					marginTop: 1,
					fontSize: size ?? 24,
					...props.iconCss,
				}}
				dangerouslySetInnerHTML={{
					__html: IconsMap?.[name || "super_success"],
				}}
				{...otherProps}
			/>
		);
	} else {
		if (!foreground || !background) throw new Error("missing foreground or background property");

		const foregroundColor = getCustomColorFromTheme(foreground.color, theme);
		const backgroundColor = getCustomColorFromTheme(background.color, theme);

		return (
			<div
				ref={ref}
				css={{
					position: "relative",
					height: size ?? 24,
					width: size ?? 24,
					display: "inline-block",
					...props.iconCss,
				}}
				{...otherProps}
			>
				<i
					css={{
						position: "absolute",
						color: foregroundColor,
						fontSize: size ?? 24,
					}}
					dangerouslySetInnerHTML={{
						__html: IconsMap?.[foreground?.name || "super_success"],
					}}
				/>
				<i
					css={{
						position: "absolute",
						color: backgroundColor,
						fontSize: size ?? 24,
					}}
					dangerouslySetInnerHTML={{
						__html: IconsMap?.[background?.name || "super_success"],
					}}
				/>
			</div>
		);
	}
});

export default IPlatformIcon;

function getCustomColorFromTheme(color: string, theme: Theme) {
	const splittedColor = color?.split(".");
	const typeColor = (splittedColor?.[0] || "primary") as any as PaletteColor;
	const shadeColor = splittedColor?.[1] || "main";

	let customColor = "black";

	if (Number(shadeColor.replace("A", ""))) {
		//@ts-ignore
		customColor = Colors[typeColor]?.[shadeColor] || color || "black";
	} else {
		//@ts-ignore
		customColor = theme.palette?.[typeColor]?.[shadeColor] || color;
	}
	return customColor;
}
